/**
 * **Account claimed** — what a member is told when a registration somebody else created for them
 * became theirs (`USR-009` AC-3, the feature specification's screens table row *Account claimed*:
 * *"Confirmation shown when an organizer-created registration is claimed"*).
 *
 * ## Why this is in the shell and not a route
 *
 * The claim resolves **on the server, during a sign-in**. `EVT-026` AC-4's sweep runs inside the
 * session enricher, flips every unclaimed row the verified address owns, and returns to a member
 * who has just pressed *Sign in* and is on their way to wherever they were going. There is no
 * moment in that sequence where a route could be the destination without taking one away: a
 * redirect to a confirmation screen would strand a member who followed an event link into the app
 * and lose the thing they came for.
 *
 * So it is a sibling of `Outlet` in `member-layout.tsx`, alongside `OfflineNotice` and
 * `UnsupportedBrowserNotice`, and for the reason that file already gives for both of them: a
 * notice each screen has to remember to render is a notice one screen will forget — and the screen
 * that forgets here is whichever one the member happened to be heading for.
 *
 * **The handoff draws no such screen.** The wireframe archive covers the member app and the
 * marketing site and has no account-claim flow at all — the same gap `set-up-page.tsx` records and
 * resolves the same way, by reusing the app's own established components rather than inventing a
 * layout. See `docs/design/handoff-corrections.md`.
 *
 * ## What it must not say
 *
 * `USR-009` AC-1 is that an unclaimed registration *grants no access until claimed*. This is the
 * sentence announcing that the claim happened, which makes it the most tempting place in the
 * product to hand over everything at once — so it deliberately hands over **nothing**. No ticket
 * token, no door token, no QR: the payload it reads carries none (see
 * `AccountClaimedRegistrationView` on the API), and what this renders is the event's name, when it
 * is, and the confirmation code the member already quotes. The ticket is one tap away on the
 * registration itself, behind their own session, where it has always been.
 *
 * ## Once, and once only
 *
 * Dismissing it POSTs an acknowledgement, and the server stamps `claimConfirmedAt` on the rows it
 * just named. That is why the read is not folded into `/mine`: a confirmation carried on a list
 * payload is re-shown by every refetch and dismissed by none. The acknowledgement is conditional
 * on the server, so two tabs open at once acknowledge one row once.
 *
 * A failed acknowledgement hides the panel anyway. The member has read it; making them read it
 * again because a network call failed is the wrong half of the trade, and the row is still there
 * to be acknowledged by the next dismissal.
 */
import { Button, Card, CardContent } from '@h2hspark/ui';
import * as React from 'react';

import { acknowledgeAccountClaimed, fetchAccountClaimed } from './api';
import { ACCOUNT_CLAIMED_COPY } from './constants';
import type { AccountClaimedRegistration } from './types';

export function AccountClaimedConfirmation({
  enabled,
}: {
  /** False for a signed-out visitor — the shell is drawn for everybody, this read is not. */
  readonly enabled: boolean;
}): React.ReactElement | null {
  const [claimed, setClaimed] = React.useState<ReadonlyArray<AccountClaimedRegistration>>([]);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let cancelled = false;
    void fetchAccountClaimed(controller.signal)
      .then((answer) => {
        if (!cancelled) setClaimed(answer.claimed);
      })
      // A read that failed is not a claim that did not happen, but it is also not something to
      // say anything about: the row keeps its `claimConfirmedAt` absent and the next page load
      // asks again.
      .catch(() => undefined);
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled]);

  if (dismissed || claimed.length === 0) return null;

  return (
    <Card
      // `role="status"` rather than `alert`: this is good news that can wait for a pause in what
      // the member is reading, and an assertive live region would interrupt a screen reader
      // mid-sentence to deliver it.
      role="status"
      aria-label={ACCOUNT_CLAIMED_COPY.title}
      className="mb-4 border-brand-200 bg-brand-50"
    >
      <CardContent className="space-y-3 pt-6">
        <h2 className="font-brand text-lg font-bold">{ACCOUNT_CLAIMED_COPY.title}</h2>
        <p className="text-sm">
          {claimed.length === 1
            ? ACCOUNT_CLAIMED_COPY.introOne
            : ACCOUNT_CLAIMED_COPY.introMany(claimed.length)}
        </p>
        <ul className="space-y-1 text-sm">
          {claimed.map((registration) => (
            <li key={registration.eventId}>
              <span className="font-semibold">{registration.eventName}</span>
              {' · '}
              {ACCOUNT_CLAIMED_COPY.confirmationLabel}: {registration.confirmationCode}
            </li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground">{ACCOUNT_CLAIMED_COPY.whereTheTicketIs}</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setDismissed(true);
            void acknowledgeAccountClaimed().catch(() => undefined);
          }}
        >
          {ACCOUNT_CLAIMED_COPY.dismiss}
        </Button>
      </CardContent>
    </Card>
  );
}
